import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Upload, FileText, Search, CalendarDays, CalendarRange, CalendarCheck, X, Zap, Sparkles, CheckCircle, AlertCircle, ArrowDownUp, FileDown, UserCog, Clock } from 'lucide-react';
import { formatDateLong, formatDateShort } from '@/utils/dateFormat';
import { DateBucketedList } from '@/components/DateBucketedList';

const AbsencesPage = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [absences, setAbsences] = useState([]);
  const [reliefAssignments, setReliefAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dateMode, setDateMode] = useState('single'); // 'single' | 'range' | 'multi'
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    end_date: '',
    dates: [],
    is_full_day: true,
    start_time: '',
    end_time: '',
    reasons: [],
    remarks: '',
    other_reason: ''
  });
  const [multiDateInput, setMultiDateInput] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [teacherPeriods, setTeacherPeriods] = useState([]);
  const [periodMaterials, setPeriodMaterials] = useState({});  // {period: {files: [], remarks: ""}}
  const [nominatedTeachers, setNominatedTeachers] = useState({});  // {period: teacher_id}
  const [busyTeachersPerPeriod, setBusyTeachersPerPeriod] = useState({}); // {period: [teacher_id,...]}
  const [partialAbsenceEnabled, setPartialAbsenceEnabled] = useState(false);
  const [affectedPeriods, setAffectedPeriods] = useState([]); // subset of today's periods when partial
  const [allTeachers, setAllTeachers] = useState([]);
  const [excludeSpecialRoles, setExcludeSpecialRoles] = useState(null);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [recentSortOrder, setRecentSortOrder] = useState('newest'); // 'newest' | 'oldest'

  // Admin: "Report on behalf of" teacher picker
  const [onBehalfTeacherId, setOnBehalfTeacherId] = useState('');
  const [teacherPickerOpen, setTeacherPickerOpen] = useState(false);
  const [teacherPickerQuery, setTeacherPickerQuery] = useState('');

  // Admin: Excel export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportRange, setExportRange] = useState(() => {
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      start_date: firstOfMonth.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
    };
  });
  const [exporting, setExporting] = useState(false);

  const handleSeedDemoAbsences = async () => {
    const confirmed = window.confirm(
      'This will REPLACE all existing absences and relief assignments with a realistic demo set.\n\n' +
      '• ~12 scenarios across the next 3 working weeks\n' +
      '• Mix of leave types: Medical, Childcare, Annual, Course, Compassionate, Hospitalisation, Others\n' +
      '• Sample PDF attachments for each: Medical Certificates, Leave Forms, Lesson Plans\n' +
      '• Auto-assigns relief duties using the live AutoAssignmentEngine\n\n' +
      'Continue?'
    );
    if (!confirmed) return;
    setSeedingDemo(true);
    try {
      const res = await axios.post(`${API}/absences/seed-demo`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Seeded ${res.data.absences_created} absences (${res.data.relief_assignments_created} relief duties) across ${res.data.teachers_affected} teachers.`);
      fetchAbsences();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to seed demo absences');
    } finally {
      setSeedingDemo(false);
    }
  };

  const filteredAbsences = useMemo(() => {
    const sorted = [...absences].sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.teacher_name || '').localeCompare(b.teacher_name || ''));
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.toLowerCase();
    return sorted.filter(a =>
      (a.teacher_name || '').toLowerCase().includes(q) ||
      (a.date || '').includes(q) ||
      (a.reasons || []).some(r => r.toLowerCase().includes(q))
    );
  }, [absences, searchQuery]);

  // Split into "newly reported (created in last 24h)" and the rest. The rest
  // is then bucketed by date phase + month inside the JSX via DateBucketedList.
  const { newlyReported, restAbsences } = useMemo(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();

    const recent = [];
    const rest = [];
    filteredAbsences.forEach(a => {
      if (a.created_at && a.created_at >= oneDayAgo) {
        recent.push(a);
      } else {
        rest.push(a);
      }
    });

    // Sort recent by created_at per user preference
    recent.sort((a, b) => {
      const da = a.created_at || '';
      const dbk = b.created_at || '';
      return recentSortOrder === 'newest'
        ? dbk.localeCompare(da)
        : da.localeCompare(dbk);
    });

    return { newlyReported: recent, restAbsences: rest };
  }, [filteredAbsences, recentSortOrder]);

  // Count relief assignments per absence_id (for the status badge).
  const reliefByAbsence = useMemo(() => {
    const map = {};
    reliefAssignments.forEach(r => {
      if (r.absence_id) {
        const bucket = map[r.absence_id] || { total: 0, acknowledged: 0 };
        bucket.total += 1;
        if (r.is_acknowledged) bucket.acknowledged += 1;
        map[r.absence_id] = bucket;
      }
    });
    return map;
  }, [reliefAssignments]);

  const renderReliefBadge = (absence) => {
    const stats = reliefByAbsence[absence.id] || { total: 0, acknowledged: 0 };
    const { total, acknowledged } = stats;
    const unassigned = absence.unassigned_periods || [];

    if (total === 0) {
      return (
        <span
          className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded inline-flex items-center gap-1"
          data-testid="absence-unassigned-badge"
        >
          <AlertCircle className="h-3 w-3" /> Relief pending
        </span>
      );
    }

    if (unassigned.length > 0) {
      return (
        <span
          className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded inline-flex items-center gap-1"
          data-testid="absence-partial-badge"
        >
          <AlertCircle className="h-3 w-3" />
          {total} relief · {unassigned.length} gap{unassigned.length === 1 ? '' : 's'}
          {acknowledged > 0 && ` · ${acknowledged} ack`}
        </span>
      );
    }

    if (acknowledged === total) {
      return (
        <span
          className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded inline-flex items-center gap-1"
          data-testid="absence-all-acknowledged-badge"
        >
          <CheckCircle className="h-3 w-3" /> All {total} acknowledged
        </span>
      );
    }

    if (acknowledged > 0) {
      return (
        <span
          className="px-2 py-0.5 bg-teal-100 text-teal-800 text-xs font-medium rounded inline-flex items-center gap-1"
          data-testid="absence-partially-acknowledged-badge"
        >
          <CheckCircle className="h-3 w-3" />
          {acknowledged}/{total} acknowledged
        </span>
      );
    }

    return (
      <span
        className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded inline-flex items-center gap-1"
        data-testid="absence-assigned-badge"
      >
        <CheckCircle className="h-3 w-3" /> {total} relief assigned
      </span>
    );
  };

  const reasonOptions = ['Medical Leave', 'Annual Leave', 'Personal Leave', 'Emergency', 'Training', 'Meeting', 'Others'];
  const OTHER_REASON = 'Others';

  useEffect(() => {
    fetchAbsences();
    // Fetch teachers for nomination dropdown (accessible to all authenticated users)
    axios.get(`${API}/users/teachers-list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setAllTeachers(res.data))
      .catch(() => {});
  }, []);

  const fetchTeacherPeriods = async (date, overrideTeacherId) => {
    if (!date) return;
    setLoadingPeriods(true);
    try {
      const tid = overrideTeacherId !== undefined
        ? overrideTeacherId
        : (user?.role === 'admin' ? onBehalfTeacherId : '');
      const params = new URLSearchParams({ date });
      if (tid) params.set('teacher_id', tid);
      const res = await axios.get(`${API}/timetables/teacher-day?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeacherPeriods(res.data);
      // Initialize period materials for each period
      const mats = {};
      res.data.forEach(p => { mats[p.period] = { files: [], remarks: '' }; });
      setPeriodMaterials(mats);
      setNominatedTeachers({});
      // Fetch busy-teachers map for this date so we can mark unavailable candidates in the nomination dropdown
      try {
        const bt = await axios.get(`${API}/relief/busy-teachers?date=${date}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBusyTeachersPerPeriod(bt.data || {});
      } catch { setBusyTeachersPerPeriod({}); }
    } catch {
      setTeacherPeriods([]);
      setBusyTeachersPerPeriod({});
    } finally {
      setLoadingPeriods(false);
    }
  };

  const fetchAbsences = async () => {
    try {
      const [absRes, reliefRes] = await Promise.all([
        axios.get(`${API}/absences`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/relief`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setAbsences(absRes.data);
      setReliefAssignments(reliefRes.data || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to load absences:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API}/files/upload`, formDataUpload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        return response.data;
      });

      const results = await Promise.all(uploadPromises);
      setUploadedFiles([...uploadedFiles, ...results]);
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handlePeriodFileUpload = async (e, period) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    try {
      const results = await Promise.all(files.map(async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/files/upload`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        return res.data;
      }));
      setPeriodMaterials(prev => ({
        ...prev,
        [period]: { ...prev[period], files: [...(prev[period]?.files || []), ...results] }
      }));
      toast.success(`${files.length} file(s) uploaded for ${period}`);
    } catch {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.reasons.length === 0) {
      toast.error('Please select at least one reason');
      return;
    }

    // Validate "Others" text
    if (formData.reasons.includes(OTHER_REASON) && !formData.other_reason.trim()) {
      toast.error('Please specify the reason under "Others"');
      return;
    }

    // Validate dates per mode
    if (dateMode === 'range') {
      if (!formData.end_date) {
        toast.error('Please select an end date for the range');
        return;
      }
      if (formData.end_date < formData.date) {
        toast.error('End date must be on or after the start date');
        return;
      }
    }
    if (dateMode === 'multi' && formData.dates.length === 0) {
      toast.error('Please add at least one date');
      return;
    }

    const isMulti = dateMode !== 'single';

    // Warn before submitting any nomination that conflicts with a busy period
    if (!isMulti) {
      const busyNominations = Object.entries(nominatedTeachers)
        .filter(([period, tid]) => tid && (busyTeachersPerPeriod[period] || []).includes(tid))
        .map(([period]) => period);
      if (busyNominations.length > 0) {
        const proceed = window.confirm(
          `You nominated unavailable teachers for: ${busyNominations.join(', ')}.\n\n` +
          'They have another class during these periods. The admin will see this and decide whether to override.\n\n' +
          'Continue submitting?'
        );
        if (!proceed) return;
      }
    }

    try {
      // Per-period materials only meaningful for single-date absences (timetable is day-specific)
      const periodMaterialsList = isMulti ? [] : teacherPeriods.map(p => ({
        period: p.period,
        class_name: p.class_name,
        subject: p.subject,
        files: (periodMaterials[p.period]?.files || []).map(f => ({ id: f.id, filename: f.filename, storage_path: f.storage_path })),
        remarks: periodMaterials[p.period]?.remarks || ''
      }));

      const nominationsList = isMulti ? [] : Object.entries(nominatedTeachers)
        .filter(([_, tid]) => tid)
        .map(([period, tid]) => {
          const teacher = allTeachers.find(t => t.id === tid);
          return { period, teacher_id: tid, teacher_name: teacher?.name || '' };
        });

      const payload = {
        date: formData.date,
        end_date: dateMode === 'range' ? formData.end_date : null,
        dates: dateMode === 'multi' ? formData.dates : null,
        is_full_day: formData.is_full_day,
        start_time: formData.start_time,
        end_time: formData.end_time,
        reasons: formData.reasons.includes(OTHER_REASON)
          ? [...formData.reasons.filter(r => r !== OTHER_REASON), `Others: ${formData.other_reason.trim()}`]
          : formData.reasons,
        remarks: formData.remarks,
        lesson_files: uploadedFiles.map(f => ({ id: f.id, filename: f.filename, storage_path: f.storage_path })),
        period_materials: periodMaterialsList,
        nominated_teachers: nominationsList,
        affected_periods: (!isMulti && partialAbsenceEnabled) ? affectedPeriods : [],
        exclude_special_roles: excludeSpecialRoles,
        auto_assign: true,
        on_behalf_of_teacher_id: user?.role === 'admin' && onBehalfTeacherId ? onBehalfTeacherId : null,
      };

      const res = await axios.post(`${API}/absences`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = res.data?.auto_assign_result;
      const count = res.data?.count || 1;

      if (count > 1) {
        if (result && result.assigned > 0 && result.unassigned === 0) {
          toast.success(`${count} absences reported. All ${result.assigned} period(s) auto-assigned!`);
        } else if (result && result.assigned > 0) {
          toast.success(`${count} absences reported. ${result.assigned} assigned, ${result.unassigned} need manual assignment.`);
        } else if (result && result.unassigned > 0) {
          toast.warning(`${count} absences reported. ${result.unassigned} period(s) need admin action.`);
        } else {
          toast.success(`${count} absences reported successfully.`);
        }
      } else if (result) {
        if (result.assigned > 0 && result.unassigned === 0) {
          toast.success(`Absence reported. ${result.assigned} relief teacher(s) auto-assigned!`);
        } else if (result.assigned > 0 && result.unassigned > 0) {
          toast.success(`Absence reported. ${result.assigned} assigned, ${result.unassigned} period(s) need manual assignment.`);
        } else if (result.unassigned > 0) {
          toast.warning(`Absence reported. ${result.unassigned} period(s) could not be auto-assigned — admin action needed.`);
        } else {
          toast.success('Absence reported successfully.');
        }
      } else {
        toast.success('Absence reported successfully.');
      }

      setDialogOpen(false);
      resetForm();
      fetchAbsences();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report absence');
    }
  };

  const resetForm = () => {
    setFormData({ date: new Date().toISOString().split('T')[0], end_date: '', dates: [], is_full_day: true, start_time: '', end_time: '', reasons: [], remarks: '', other_reason: '' });
    setPartialAbsenceEnabled(false);
    setAffectedPeriods([]);
    setDateMode('single');
    setMultiDateInput('');
    setUploadedFiles([]);
    setTeacherPeriods([]);
    setPeriodMaterials({});
    setNominatedTeachers({});
    setExcludeSpecialRoles(null);
    setOnBehalfTeacherId('');
    setTeacherPickerQuery('');
  };

  const handleExportAbsences = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/absences/export/excel?start_date=${exportRange.start_date}&end_date=${exportRange.end_date}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `absences_${exportRange.start_date}_to_${exportRange.end_date}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Absences exported to Excel');
      setExportDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export absences');
    } finally {
      setExporting(false);
    }
  };

  const pickMonth = (offset) => {
    // offset 0 = current month, -1 = last month, etc.
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    setExportRange({
      start_date: first.toISOString().split('T')[0],
      end_date: last.toISOString().split('T')[0],
    });
  };

  const toggleReason = (reason) => {
    setFormData(prev => ({
      ...prev,
      reasons: prev.reasons.includes(reason)
        ? prev.reasons.filter(r => r !== reason)
        : [...prev.reasons, reason]
    }));
  };

  // Compute effective dates preview for the summary strip
  const effectiveDates = useMemo(() => {
    if (dateMode === 'single') return formData.date ? [formData.date] : [];
    if (dateMode === 'range') {
      if (!formData.date || !formData.end_date || formData.end_date < formData.date) return [];
      const out = [];
      const start = new Date(formData.date + 'T00:00:00');
      const end = new Date(formData.end_date + 'T00:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        out.push(d.toISOString().split('T')[0]);
      }
      return out;
    }
    return [...formData.dates].sort();
  }, [dateMode, formData.date, formData.end_date, formData.dates]);

  const addMultiDate = () => {
    if (!multiDateInput) return;
    if (formData.dates.includes(multiDateInput)) {
      toast.info('Date already added');
      return;
    }
    setFormData(prev => ({ ...prev, dates: [...prev.dates, multiDateInput].sort() }));
    setMultiDateInput('');
  };

  const removeMultiDate = (d) => {
    setFormData(prev => ({ ...prev, dates: prev.dates.filter(x => x !== d) }));
  };

  const handleModeChange = (mode) => {
    setDateMode(mode);
    // Clear per-period state when leaving single mode (not applicable)
    if (mode !== 'single') {
      setTeacherPeriods([]);
      setPeriodMaterials({});
      setNominatedTeachers({});
    } else if (formData.date) {
      fetchTeacherPeriods(formData.date);
    }
  };

  return (
    <div data-testid="absences-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900">
            {user?.role === 'admin' ? 'Staff Absences' : 'My Absences'}
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-2">
            {user?.role === 'admin' ? 'Report and manage staff absences' : 'View and report your absences'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {user?.role === 'admin' && (
            <Button
              variant="outline"
              className="border-slate-300 w-full sm:w-auto"
              onClick={() => setExportDialogOpen(true)}
              data-testid="export-absences-excel-button"
            >
              <FileDown className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
          )}
          {user?.role === 'admin' && (
            <Button
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 w-full sm:w-auto"
              onClick={handleSeedDemoAbsences}
              disabled={seedingDemo}
              data-testid="seed-demo-absences-button"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {seedingDemo ? 'Seeding...' : 'Seed Demo Absences'}
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 w-full sm:w-auto" data-testid="report-absence-button">
                <Plus className="mr-2 h-4 w-4" />
                Report Absence
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Report Staff Absence</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Admin: Report on behalf of a teacher */}
              {user?.role === 'admin' && (
                <div className="p-3 bg-blue-50/60 border border-blue-100 rounded-md relative" data-testid="on-behalf-picker">
                  <Label className="text-xs font-semibold text-blue-900 flex items-center gap-1.5 mb-2">
                    <UserCog className="h-3.5 w-3.5" />
                    Select teacher to report absence for
                  </Label>
                  <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2 inline-flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    As admin, you cannot report an absence for yourself. Select a teacher below.
                  </p>
                  {(() => {
                    const selected = onBehalfTeacherId
                      ? allTeachers.find(t => t.id === onBehalfTeacherId)
                      : null;
                    return selected ? (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-300 rounded-md text-sm" data-testid="on-behalf-selected-chip">
                        <span className="font-medium text-blue-900">{selected.name}</span>
                        <span className="text-[11px] text-slate-500 uppercase">{selected.role}</span>
                        <button
                          type="button"
                          onClick={() => { setOnBehalfTeacherId(''); setTeacherPickerQuery(''); setTeacherPeriods([]); }}
                          className="hover:text-red-700 text-slate-400"
                          aria-label="Clear selection"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Input
                          placeholder="Type teacher name to search..."
                          value={teacherPickerQuery}
                          onChange={(e) => { setTeacherPickerQuery(e.target.value); setTeacherPickerOpen(true); }}
                          onFocus={() => setTeacherPickerOpen(true)}
                          onBlur={() => setTimeout(() => setTeacherPickerOpen(false), 150)}
                          className="bg-white"
                          data-testid="on-behalf-search-input"
                        />
                        {teacherPickerOpen && teacherPickerQuery.trim() && (() => {
                          const q = teacherPickerQuery.toLowerCase();
                          const matches = allTeachers
                            .filter(t => t.name.toLowerCase().includes(q) || (t.email || '').toLowerCase().includes(q))
                            .slice(0, 8);
                          if (matches.length === 0) {
                            return <div className="absolute z-30 left-3 right-3 mt-1 bg-white border border-slate-200 rounded-md shadow-lg px-3 py-2 text-xs text-slate-500">No teachers match "{teacherPickerQuery}"</div>;
                          }
                          return (
                            <div className="absolute z-30 left-3 right-3 mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg" data-testid="on-behalf-suggestions">
                              {matches.map(t => (
                                <button
                                  key={t.id}
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => { setOnBehalfTeacherId(t.id); setTeacherPickerOpen(false); setTeacherPickerQuery(''); if (dateMode === 'single' && formData.date) fetchTeacherPeriods(formData.date, t.id); }}
                                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 hover:text-blue-900"
                                  data-testid={`on-behalf-option-${t.id}`}
                                >
                                  <span className="font-medium">{t.name}</span>
                                  <span className="ml-2 text-[11px] text-slate-500 uppercase">{t.role}</span>
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Date Mode Selector */}
              <div>
                <Label className="mb-2 block">Absence Period</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="date-mode-selector">
                  <button
                    type="button"
                    onClick={() => handleModeChange('single')}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${dateMode === 'single' ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                    data-testid="mode-single"
                  >
                    <CalendarDays className="h-4 w-4" /> Single Day
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModeChange('range')}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${dateMode === 'range' ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                    data-testid="mode-range"
                  >
                    <CalendarRange className="h-4 w-4" /> Date Range
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModeChange('multi')}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${dateMode === 'multi' ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                    data-testid="mode-multi"
                  >
                    <CalendarCheck className="h-4 w-4" /> Multiple Days
                  </button>
                </div>
              </div>

              {/* Conditional Date Inputs */}
              {dateMode === 'single' && (
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => {
                      setFormData({ ...formData, date: e.target.value });
                      fetchTeacherPeriods(e.target.value);
                    }}
                    required
                    className="mt-1.5"
                    data-testid="absence-date-input"
                  />
                </div>
              )}

              {dateMode === 'range' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="range-start">Start Date</Label>
                    <Input
                      id="range-start"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="mt-1.5"
                      data-testid="range-start-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="range-end">End Date (inclusive)</Label>
                    <Input
                      id="range-end"
                      type="date"
                      value={formData.end_date}
                      min={formData.date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                      className="mt-1.5"
                      data-testid="range-end-input"
                    />
                  </div>
                </div>
              )}

              {dateMode === 'multi' && (
                <div>
                  <Label htmlFor="multi-date">Add Date(s)</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      id="multi-date"
                      type="date"
                      value={multiDateInput}
                      onChange={(e) => setMultiDateInput(e.target.value)}
                      className="flex-1"
                      data-testid="multi-date-input"
                    />
                    <Button type="button" variant="outline" onClick={addMultiDate} data-testid="multi-date-add">
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </div>
                  {formData.dates.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="multi-date-chips">
                      {formData.dates.map(d => (
                        <span key={d} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                          {formatDateShort(d)}
                          <button type="button" onClick={() => removeMultiDate(d)} className="hover:text-blue-900" data-testid={`remove-date-${d}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Effective dates summary */}
              {effectiveDates.length > 1 && (
                <div className="p-2.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900" data-testid="effective-dates-summary">
                  <strong>{effectiveDates.length}</strong> absence record(s) will be created — one per date. Auto-assignment runs for each day's timetable.
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="full-day"
                  checked={formData.is_full_day}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_full_day: checked })}
                  data-testid="full-day-checkbox"
                />
                <Label htmlFor="full-day" className="cursor-pointer">Full Day Absence</Label>
              </div>

              {!formData.is_full_day && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start-time">Start Time</Label>
                    <Input
                      id="start-time"
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      className="mt-1.5"
                      data-testid="start-time-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-time">End Time</Label>
                    <Input
                      id="end-time"
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      className="mt-1.5"
                      data-testid="end-time-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label>Reason(s)</Label>
                <div className="mt-2 space-y-2">
                  {reasonOptions.map((reason) => (
                    <div key={reason} className="flex items-center space-x-2">
                      <Checkbox
                        id={reason}
                        checked={formData.reasons.includes(reason)}
                        onCheckedChange={() => toggleReason(reason)}
                        data-testid={`reason-${reason.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      <Label htmlFor={reason} className="cursor-pointer">{reason}</Label>
                    </div>
                  ))}
                </div>
                {formData.reasons.includes(OTHER_REASON) && (
                  <Input
                    value={formData.other_reason}
                    onChange={(e) => setFormData({ ...formData, other_reason: e.target.value })}
                    placeholder="Please specify the reason..."
                    className="mt-2"
                    data-testid="other-reason-input"
                  />
                )}
              </div>

              <div>
                <Label htmlFor="remarks">General Remarks for Relief Teacher</Label>
                <Textarea
                  id="remarks"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="Any special instructions or notes..."
                  className="mt-1.5"
                  rows={2}
                  data-testid="remarks-textarea"
                />
              </div>

              {/* Per-Period Materials & Nominations */}
              {loadingPeriods && <p className="text-sm text-slate-400">Loading your timetable...</p>}
              {teacherPeriods.length > 0 && (
                <div className="space-y-3" data-testid="period-materials-section">
                  <Label className="text-base font-semibold">Your Periods for {new Date(formData.date + 'T00:00:00').toLocaleDateString('en-SG', { weekday: 'long', day: 'numeric', month: 'short' })}</Label>
                  <p className="text-xs text-slate-500">Upload lesson materials for each period and optionally nominate a relief teacher.</p>

                  {/* Partial absence toggle — lets user restrict relief to specific periods */}
                  <div className="flex items-start sm:items-center gap-3 p-3 rounded-md border border-amber-200 bg-amber-50/70">
                    <Switch
                      checked={partialAbsenceEnabled}
                      onCheckedChange={(checked) => {
                        setPartialAbsenceEnabled(checked);
                        if (checked) {
                          setAffectedPeriods(teacherPeriods.map(p => p.period));
                        } else {
                          setAffectedPeriods([]);
                        }
                      }}
                      data-testid="partial-absence-toggle"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-900">Only absent for specific periods</p>
                      <p className="text-[11px] text-amber-700/80">
                        Toggle on if you&apos;ll teach part of the day. Relief will only be arranged for the period(s) you tick below.
                      </p>
                    </div>
                    {partialAbsenceEnabled && (
                      <span className="text-xs font-semibold text-amber-800" data-testid="affected-periods-count">
                        {affectedPeriods.length} / {teacherPeriods.length} period{teacherPeriods.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>

                  {teacherPeriods.map((p) => {
                    const isAffected = !partialAbsenceEnabled || affectedPeriods.includes(p.period);
                    return (
                    <div key={p.period} className={`p-3 border rounded-lg ${isAffected ? 'border-slate-200 bg-slate-50/50' : 'border-slate-200 bg-slate-100/40 opacity-60'}`} data-testid="period-row">
                      <div className="flex items-center gap-2 mb-2">
                        {partialAbsenceEnabled && (
                          <input
                            type="checkbox"
                            checked={affectedPeriods.includes(p.period)}
                            onChange={(e) => {
                              setAffectedPeriods(prev => e.target.checked
                                ? Array.from(new Set([...prev, p.period]))
                                : prev.filter(x => x !== p.period));
                            }}
                            className="h-4 w-4 accent-amber-600"
                            data-testid={`affected-period-checkbox-${p.period}`}
                            aria-label={`Mark ${p.period} as absent`}
                          />
                        )}
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-semibold rounded">{p.period}</span>
                        <span className="text-sm font-medium text-slate-900">{p.class_name}</span>
                        <span className="text-sm text-slate-500">({p.subject})</span>
                        {partialAbsenceEnabled && !isAffected && (
                          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-semibold uppercase tracking-wide">Teaching</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Lesson Materials</Label>
                          <label className="cursor-pointer block mt-1">
                            <div className="flex items-center justify-center px-3 py-2 border border-dashed border-slate-300 rounded-md hover:border-slate-400 transition-colors text-xs text-slate-500">
                              <Upload className="mr-1.5 h-3 w-3" />
                              {uploading ? 'Uploading...' : 'Upload files'}
                            </div>
                            <input type="file" multiple onChange={(e) => handlePeriodFileUpload(e, p.period)} className="hidden" data-testid={`period-file-${p.period}`} />
                          </label>
                          {(periodMaterials[p.period]?.files || []).length > 0 && (
                            <div className="mt-1 space-y-0.5">
                              {periodMaterials[p.period].files.map(f => (
                                <div key={f.id} className="flex items-center text-xs text-slate-600"><FileText className="mr-1 h-3 w-3" />{f.filename}</div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Nominate Relief Teacher (optional)</Label>
                          {(() => {
                            const busyIds = new Set(busyTeachersPerPeriod[p.period] || []);
                            const selectedId = nominatedTeachers[p.period] || '';
                            const isSelectedBusy = selectedId && busyIds.has(selectedId);
                            return (
                              <>
                                <select
                                  value={selectedId}
                                  onChange={(e) => setNominatedTeachers(prev => ({ ...prev, [p.period]: e.target.value }))}
                                  className={`mt-1 w-full h-9 px-2 border rounded-md text-sm bg-white ${isSelectedBusy ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}
                                  data-testid={`nominate-${p.period}`}
                                >
                                  <option value="">Auto-assign</option>
                                  {allTeachers
                                    .filter(t => t.id !== user?.id)
                                    .map(t => ({ ...t, busy: busyIds.has(t.id) }))
                                    .sort((a, b) => (a.busy === b.busy ? a.name.localeCompare(b.name) : a.busy - b.busy))
                                    .map(t => (
                                      <option key={t.id} value={t.id} style={t.busy ? { color: '#b91c1c' } : undefined}>
                                        {t.busy ? '\u26A0 ' : '\u2713 '}{t.name}{t.busy ? ' (Busy)' : ''}
                                      </option>
                                    ))}
                                </select>
                                {isSelectedBusy && (
                                  <p className="text-[10px] text-red-600 mt-1 flex items-start gap-1" data-testid={`nominate-warning-${p.period}`}>
                                    <span className="font-semibold">Unavailable:</span>
                                    <span>This teacher has another class during {p.period}. You can still submit — admin will review.</span>
                                  </p>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="mt-2">
                        <Input
                          value={periodMaterials[p.period]?.remarks || ''}
                          onChange={(e) => setPeriodMaterials(prev => ({ ...prev, [p.period]: { ...prev[p.period], remarks: e.target.value } }))}
                          placeholder="Remarks for this period..."
                          className="h-8 text-xs"
                          data-testid={`period-remarks-${p.period}`}
                        />
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
              {teacherPeriods.length === 0 && !loadingPeriods && formData.date && (
                <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                  <p className="text-sm text-slate-500">No timetable entries found for this date. General file upload:</p>
                  <label className="cursor-pointer block mt-2">
                    <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-300 rounded-md hover:border-slate-400">
                      <Upload className="mr-2 h-4 w-4 text-slate-500" />
                      <span className="text-sm text-slate-600">{uploading ? 'Uploading...' : 'Click to upload files'}</span>
                    </div>
                    <input type="file" multiple onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
                  </label>
                  {uploadedFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {uploadedFiles.map((file) => (
                        <div key={file.id} className="flex items-center text-sm text-slate-600"><FileText className="mr-2 h-3 w-3" />{file.filename}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Exclude HOD/Special option */}
              <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-md border border-slate-200">
                <Checkbox
                  id="exclude-special"
                  checked={excludeSpecialRoles === true}
                  onCheckedChange={(checked) => setExcludeSpecialRoles(checked ? true : null)}
                  data-testid="exclude-special-checkbox"
                />
                <Label htmlFor="exclude-special" className="text-sm cursor-pointer">Exclude HODs / Special Teachers from auto-assignment</Label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-button">
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-900 hover:bg-blue-800" data-testid="submit-absence-button">
                  Submit Absence
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-600">Loading absences...</div>
      ) : absences.length === 0 ? (
        <Card className="p-8 text-center" data-testid="no-absences-message">
          <p className="text-slate-500">No absences reported yet.</p>
        </Card>
      ) : (
        <>
        <div className="relative mb-4" data-testid="absence-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by teacher name, date, or reason..." className="pl-9" data-testid="absence-search-input" />
        </div>
        {filteredAbsences.length === 0 ? (
          <Card className="p-8 text-center"><p className="text-slate-500">No absences matching "{searchQuery}"</p></Card>
        ) : (
        <div className="space-y-6">
          {/* Newly Reported Section */}
          {newlyReported.length > 0 && (
            <div data-testid="newly-reported-section">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Newly Reported (Last 24 Hours)</h3>
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">{newlyReported.length}</span>
                </div>
                <button
                  onClick={() => setRecentSortOrder(o => o === 'newest' ? 'oldest' : 'newest')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                  data-testid="recent-sort-toggle"
                  title="Toggle sort order"
                >
                  <ArrowDownUp className="h-3.5 w-3.5" />
                  {recentSortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
                </button>
              </div>
              <div className="space-y-3">
                {newlyReported.map((absence) => (
                  <Card key={absence.id} className="dashboard-card p-4 sm:p-6 border-l-4 border-l-emerald-400" data-testid="absence-card">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                            <h3 className="text-base sm:text-lg font-semibold text-slate-900">{absence.teacher_name}</h3>
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">New</span>
                            {renderReliefBadge(absence)}
                          </div>
                          <div className="space-y-1 text-sm text-slate-600">
                            <p><strong>Date:</strong> {formatDateLong(absence.date)}</p>
                            <p><strong>Type:</strong> {absence.is_full_day ? 'Full Day' : `${absence.start_time} - ${absence.end_time}`}</p>
                            <p><strong>Reason(s):</strong> {absence.reasons.join(', ')}</p>
                            {absence.created_at && (
                              <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="absence-applied-at">
                                <Clock className="h-3 w-3" />
                                Applied {new Date(absence.created_at).toLocaleString('en-SG', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                                {absence.reported_by_admin_name && <span className="italic ml-1">· by {absence.reported_by_admin_name}</span>}
                              </p>
                            )}
                            {absence.remarks && <p><strong>Remarks:</strong> {absence.remarks}</p>}
                            {absence.lesson_files && absence.lesson_files.length > 0 && (
                              <div className="mt-2">
                                <strong>Lesson Files:</strong>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {absence.lesson_files.map((file) => (
                                    <a key={file.id} href={`${API}/files/${file.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 transition-colors" data-testid="lesson-file-link">
                                      <FileText className="h-3 w-3" />{file.filename}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        {user?.role === 'admin' && (
                          <div className="flex flex-col gap-2 sm:items-end">
                            <Button
                              size="sm"
                              onClick={() => navigate(`/relief?absence_id=${absence.id}&mode=smart`)}
                              className="bg-blue-900 hover:bg-blue-800 text-white"
                              data-testid={`smart-assign-relief-${absence.id}`}
                            >
                              <Zap className="h-3.5 w-3.5 mr-1.5" />
                              Smart Assign Relief
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Date-Bucketed Sections (Upcoming → Last 3 Days → Expired, by month) */}
          <DateBucketedList
            items={restAbsences}
            getDate={(a) => a.date}
            testidPrefix="absences-bucket"
            emptyLabel="No older absences."
            renderItem={(absence) => (
            <Card key={absence.id} className="dashboard-card p-4 sm:p-6" data-testid="absence-card">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <h3 className="text-base sm:text-lg font-semibold text-slate-900">{absence.teacher_name}</h3>
                      {renderReliefBadge(absence)}
                    </div>
                    <div className="space-y-1 text-sm text-slate-600">
                      <p><strong>Date:</strong> {formatDateLong(absence.date)}</p>
                      <p><strong>Type:</strong> {absence.is_full_day ? 'Full Day' : `${absence.start_time} - ${absence.end_time}`}</p>
                      <p><strong>Reason(s):</strong> {absence.reasons.join(', ')}</p>
                      {absence.created_at && (
                        <p className="text-xs text-slate-500 flex items-center gap-1" data-testid="absence-applied-at">
                          <Clock className="h-3 w-3" />
                          Applied {new Date(absence.created_at).toLocaleString('en-SG', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                          {absence.reported_by_admin_name && <span className="italic ml-1">· by {absence.reported_by_admin_name}</span>}
                        </p>
                      )}
                      {absence.remarks && <p><strong>Remarks:</strong> {absence.remarks}</p>}
                      {absence.lesson_files && absence.lesson_files.length > 0 && (
                        <div className="mt-2">
                          <strong>Lesson Files:</strong>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {absence.lesson_files.map((file) => (
                              <a key={file.id} href={`${API}/files/${file.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 transition-colors" data-testid="lesson-file-link">
                                <FileText className="h-3 w-3" />{file.filename}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {user?.role === 'admin' && (
                    <div className="flex flex-col gap-2 sm:items-end">
                      <Button
                        size="sm"
                        onClick={() => navigate(`/relief?absence_id=${absence.id}&mode=smart`)}
                        className="bg-blue-900 hover:bg-blue-800 text-white"
                        data-testid={`smart-assign-relief-${absence.id}`}
                      >
                        <Zap className="h-3.5 w-3.5 mr-1.5" />
                        Smart Assign Relief
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
            )}
          />
        </div>
        )}
        </>
      )}

      {/* Admin — Export Absences to Excel Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="absences-export-dialog">
          <DialogHeader>
            <DialogTitle>Export Absences to Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Quick pick — month</Label>
              <div className="flex flex-wrap gap-2" data-testid="export-month-shortcuts">
                {[
                  { label: 'This month', offset: 0 },
                  { label: 'Last month', offset: -1 },
                  { label: '2 months ago', offset: -2 },
                  { label: '3 months ago', offset: -3 },
                ].map(p => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => pickMonth(p.offset)}
                    className="px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors"
                    data-testid={`export-month-${p.offset}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Start Date</Label>
                <Input
                  type="date"
                  value={exportRange.start_date}
                  onChange={(e) => setExportRange({ ...exportRange, start_date: e.target.value })}
                  className="mt-1"
                  data-testid="export-start-date"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">End Date</Label>
                <Input
                  type="date"
                  value={exportRange.end_date}
                  onChange={(e) => setExportRange({ ...exportRange, end_date: e.target.value })}
                  className="mt-1"
                  data-testid="export-end-date"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-400">
              The Excel file includes date, teacher, day type, reasons, status, applied-at, reported-by, relief coverage, and remarks.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => setExportDialogOpen(false)} disabled={exporting}>Cancel</Button>
              <Button
                onClick={handleExportAbsences}
                disabled={exporting || !exportRange.start_date || !exportRange.end_date || exportRange.end_date < exportRange.start_date}
                className="bg-blue-900 hover:bg-blue-800"
                data-testid="export-absences-submit-button"
              >
                <FileDown className="mr-2 h-4 w-4" />
                {exporting ? 'Exporting...' : 'Download Excel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AbsencesPage;